import * as React from 'react'
import { isEqual } from 'lodash'
import * as qiniu from 'qiniu-js'
import { inject, observer } from 'mobx-react'
import { Form, Button, Input, Upload, Icon, Progress, Row, Col } from 'antd'
import { ComponentExt } from 'util/reactExt'
import { FormComponentProps } from 'antd/lib/form'
import { taskType, UPLOAD_URL, BaseURL } from 'constants/index'
import { qnToken } from 'services/api/upload'
import { utf8ToBase64 } from 'util/utils'

const FormItem = Form.Item
const TextArea = Input.TextArea
const formItemLayout = {
  labelCol: { span: 4 },
  wrapperCol: { span: 14 },
}

function hasErrors(fieldsError) {
  return Object.keys(fieldsError).some(field => fieldsError[field])
}

interface IConfigContentEditZoneProps extends FormComponentProps {
  levelStore?: any
  globalStore?: any
  onRef?: (ref) => void
}

interface IConfigContentEditZoneState {
  progressPercent: number
  progressStatus: string // 'normal' | 'exception' | 'active' | 'success'
  content: string
  course_time: number
  video: any
  prevCurrentConfigContent: any
  filename: string
  filesize: number
}

@inject('globalStore', 'levelStore')
@observer
class ConfigContentEditZone extends ComponentExt<
  IConfigContentEditZoneProps,
  IConfigContentEditZoneState
> {
  state = {
    progressPercent: 0,
    progressStatus: 'normal',
    content: '',
    course_time: 0,
    video: null,
    prevCurrentConfigContent: null,
    filename: '',
    filesize: null,
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const {
      levelStore: { currentConfigContent },
    } = nextProps

    if (
      currentConfigContent &&
      !isEqual(prevState.prevCurrentConfigContent, currentConfigContent)
    ) {
      return {
        prevCurrentConfigContent: currentConfigContent,
        content: currentConfigContent.content,
        course_time: currentConfigContent.course_time,
        video: null,
        filename: currentConfigContent.file_name,
        filesize: currentConfigContent.file_size,
        progressPercent: 100,
        progressStatus: 'success',
      }
    } else if (!currentConfigContent) {
      return {
        prevCurrentConfigContent: null,
      }
    }

    return null
  }

  componentDidMount() {
    this.props.onRef(this)
  }

  get isVideo() {
    const {
      levelStore: { currentConfigContentType },
    } = this.props
    return currentConfigContentType === taskType.video
  }

  handleSubmit = e => {
    const { content, course_time, video, filename, filesize } = this.state
    const {
      levelStore: { currentConfigContent },
      form,
    } = this.props
    e.preventDefault()
    const isEdit = !!currentConfigContent
    form.validateFields(async (err, values) => {
      if (!err) {
        if (this.isVideo) {
          const payload: any = {
            ...values,
            content,
            course_time,
            video,
            file_name: filename,
            file_size: filesize,
          }

          if (isEdit) {
            payload.id = currentConfigContent.id
          }

          if (!content) {
            return this.$message.info('请上传视频内容')
          } else {
            await this.api.level.postAdvancedVideo(payload)
            this.$message.success(`视频：${values.title}发布成功`)
          }
        } else {
          const payload = {
            content: values.title,
            answers: [values.content],
          }

          if (!values.content.trim()) {
            return this.$message.info('请输入参考答案')
          }

          if (isEdit) {
            await this.api.level.updateAdvancedExample(
              currentConfigContent.id,
              payload,
            )
          } else {
            await this.api.level.addAdvancedExample(payload)
          }

          this.$message.success(`试题：${values.title}发布成功`)
        }

        if (!isEdit) {
          form.resetFields()
          this.setState({
            progressStatus: 'normal',
            progressPercent: 0,
            filename: '',
            filesize: null,
            content: '',
          })
        }
      }
    })
  }

  getUpLoadFileName = file => {
    const firstName = file.name.split('.')[0]
    const type = file.name.split('.')[1]
    const time = `${new Date().getTime()}`

    return `${firstName}_${time}.${type}`
  }

  customRequest = async (info): Promise<any> => {
    const file = info.file
    const key = utf8ToBase64(this.getUpLoadFileName(file))
    const res = await qnToken(UPLOAD_URL, BaseURL)
    const {
      data: {
        data: { token, domain },
      },
    } = res
    const putExtra = {
      fname: key,
      params: {},
      mimeType: null,
    }
    const config = {
      useCdnDomain: true,
      // region: qiniu.region.z0,
    }
    const observable = qiniu.upload(file, key, token)
    const observer = {
      next(result) {
        info.onProgress(result.total, info.file)
      },
      error: error => {
        info.onError(error)
      },
      complete: async result => {
        result.domain = domain
        info.onSuccess(result, file)
      },
    }
    observable.subscribe(observer)
  }

  removeVideo = () => {
    this.setState({
      progressPercent: 0,
      progressStatus: 'normal',
      course_time: 0,
      content: '',
      video: null,
      filename: '',
      filesize: null,
    })
  }

  resetForm = () => {
    this.props.form.resetFields()
    this.removeVideo()
  }

  renderExampleComponent = () => {
    const {
      levelStore: { currentConfigContent },
    } = this.props
    const { getFieldDecorator } = this.props.form

    return (
      <>
        <FormItem {...formItemLayout} label={'题目标题'}>
          {getFieldDecorator('title', {
            initialValue:
              currentConfigContent &&
              (this.isVideo
                ? currentConfigContent.title
                : currentConfigContent.content),
            rules: [{ required: true, message: '请输入题目标题' }],
          })(<Input />)}
        </FormItem>
        <FormItem {...formItemLayout} label={'参考答案'}>
          {getFieldDecorator('content', {
            initialValue:
              currentConfigContent &&
              (this.isVideo
                ? currentConfigContent.content
                : currentConfigContent.answers &&
                  currentConfigContent.answers[0]),
            rules: [{ required: true, message: '请填写参考答案' }],
          })(<TextArea rows={4} />)}
        </FormItem>
      </>
    )
  }

  renderVideoComponent = () => {
    const { progressPercent, progressStatus, filename, filesize } = this.state
    const {
      levelStore: { currentConfigContent },
    } = this.props
    const { getFieldDecorator } = this.props.form
    const uploadProps = {
      accept: 'video/mp4',
      // accept: '*',
      action: '',
      customRequest: this.customRequest,
      onStart: file => {
        this.setState({
          progressStatus: 'active',
          progressPercent: 0,
          filename: file.name,
          filesize: +(file.size / 1024 ** 2).toFixed(2),
        })
      },
      onSuccess: ret => {
        this.setState({
          content: `${ret.domain}/${ret.key}`,
          course_time: ret.duration,
          video: JSON.stringify(ret),
        })
        this.setState({
          progressStatus: 'success',
        })
      },
      onProgress: (step, file) => {
        this.setState({
          progressPercent: Math.round(step.percent),
        })
      },
      onError: err => {
        this.setState({
          progressStatus: 'exception',
        })
        this.$message.error('视频上传失败，' + err.message)
      },
    }

    // @ts-ignore
    return (
      <>
        <FormItem {...formItemLayout} label={'视频标题'}>
          {getFieldDecorator('title', {
            initialValue: currentConfigContent
              ? currentConfigContent.title
              : '',
            rules: [{ required: true, message: '请输入视频标题' }],
          })(<Input />)}
        </FormItem>
        <FormItem {...formItemLayout} label={'视频简介'}>
          {getFieldDecorator('article_desc', {
            initialValue: currentConfigContent
              ? currentConfigContent.article_desc
              : '',
            rules: [{ required: true, message: '请输入视频简介' }],
          })(<TextArea rows={4} />)}
        </FormItem>
        <FormItem
          {...formItemLayout}
          colon={false}
          label={
            <div style={{ marginRight: 2 }}>
              <p>
                <span style={{ color: 'red' }}>*</span>导入视频：
              </p>
              <p>(只支持 mp4 格式)</p>
            </div>
          }
        >
          <Row>
            <Col span={4}>
              <Upload accept="video/*" listType="picture" {...uploadProps}>
                <Button>
                  <Icon type="upload" />
                  浏览
                </Button>
              </Upload>
            </Col>
            <Col offset={1} span={4}>
              {progressPercent === 100 && (
                <Button onClick={this.removeVideo}>
                  <Icon type="upload" />
                  移除
                </Button>
              )}
            </Col>
          </Row>
          {progressPercent !== 0 && (
            <>
              <Progress
                percent={progressPercent}
                status={
                  progressStatus as
                    | 'success'
                    | 'normal'
                    | 'active'
                    | 'exception'
                }
              />
              <div>
                <span>文件名称：{filename} </span>
                <span>文件大小：{filesize} MB</span>
              </div>
            </>
          )}
        </FormItem>
      </>
    )
  }

  render() {
    const {
      levelStore: { currentConfigContentType, configContentMode },
    } = this.props
    const { getFieldsError } = this.props.form
    const components = {
      [taskType.example]: this.renderExampleComponent,
      [taskType.video]: this.renderVideoComponent,
    }

    const btnTxt =
      currentConfigContentType === taskType.example ? '题目' : '视频'

    return (
      <Form onSubmit={this.handleSubmit}>
        {currentConfigContentType && components[currentConfigContentType]()}
        {
          <FormItem {...formItemLayout}>
            <Button
              type="primary"
              htmlType="submit"
              disabled={hasErrors(getFieldsError())}
            >{`${configContentMode}${btnTxt}`}</Button>
          </FormItem>
        }
      </Form>
    )
  }
}

export default Form.create()(ConfigContentEditZone)
